import type { ExerciseTypeConfig } from '../registry/types';
import type {
  FillInBlankExerciseDefinition,
  FillInBlankExerciseSubmission,
  FillInBlankEvaluationDetail
} from '../types';
import { fillInBlankSubmissionSchema } from '../types/schemas';
import { damerauLevenshtein, normalizeAnswer as normalizeText } from '../text/normalize';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: FillInBlankExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

function normalizeAnswer(value: string | undefined | null): string {
  return normalizeText(value);
}

// Only "perfect" counts as a pass; "partial" surfaces as a yellow "almost" hint.
type BlankGrade = 'perfect' | 'partial' | 'wrong';

// A non-exact answer is treated as a typo (partial) only when the edit distance is
// small in BOTH absolute and length-relative terms — a 1-char slip is a typo in
// "accommodation" but a different word in "cat".
const MAX_TYPO_DISTANCE = 2;
const MAX_TYPO_RELATIVE = 0.34;

function splitExpectedAnswers(value: string | undefined | null): string[] {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return [];
  return trimmed
    .split(',')
    .map((item) => normalizeAnswer(item))
    .filter((item) => item.length > 0);
}

function isPassingGrade(grade: BlankGrade): boolean {
  return grade === 'perfect';
}

/**
 * FillInBlank exercise type configuration
 */
export const fillInBlankConfig: ExerciseTypeConfig<
  'FillInBlank',
  FillInBlankExerciseDefinition,
  FillInBlankExerciseSubmission,
  FillInBlankEvaluationDetail
> = {
  type: 'FillInBlank',
  isAutoGradable: true,
  submissionSchema: fillInBlankSubmissionSchema,

  defaultMaxScore: (definition) => {
    const totalBlanks = definition.sentences.reduce(
      (count, sentence) => count + (sentence.blanks?.length ?? 0),
      0
    );
    return getNormalizedMaxScore(definition, Math.max(1, totalBlanks));
  },

  evaluate: (definition, submission) => {
    const expectedSentences = definition.sentences ?? [];
    const providedAnswers = submission.answers ?? [];

    const sentenceEvaluations = expectedSentences.map((sentence, sentenceIndex) => {
      const blanks = sentence.blanks ?? [];
      const providedSentenceAnswers = providedAnswers[sentenceIndex] ?? [];
      const normalizedAnswers = Array.isArray(providedSentenceAnswers)
        ? providedSentenceAnswers
        : [providedSentenceAnswers];

      const defaultBlank = { blankId: undefined, answer: '' };
      const blankEvaluations = (blanks.length ? blanks : [defaultBlank]).map(
        (blank: any, blankIndex: number) => {
          const expectedRaw = normalizeAnswer(blank.answer);
          const expectedCandidates = splitExpectedAnswers(blank.answer);
          const provided = normalizeAnswer(normalizedAnswers[blankIndex] as string);
          const candidates = expectedCandidates.length > 0 ? expectedCandidates : [''];

          let bestGrade: BlankGrade = 'wrong';
          let bestReason = '';
          let bestExpected = candidates[0] ?? '';
          let bestDistance = Number.POSITIVE_INFINITY;

          candidates.forEach((expected) => {
            const distance = damerauLevenshtein(expected, provided);
            const maxLen = Math.max(expected.length, provided.length, 1);
            const relativeDistance = distance / maxLen;
            let grade: BlankGrade;
            let reason = '';

            if (expected === provided) {
              grade = 'perfect';
              reason = 'Exact match';
            } else if (!expected && !provided) {
              grade = 'perfect';
              reason = 'Exact match';
            } else if (!provided && expected) {
              // Empty answer when something was expected
              grade = 'wrong';
              reason = 'Provided answer is empty';
            } else if (distance <= MAX_TYPO_DISTANCE && relativeDistance <= MAX_TYPO_RELATIVE) {
              grade = 'partial';
              reason = `Possible typo: expected "${expected}", got "${provided}"`;
            } else {
              // Answer provided but doesn't match
              grade = 'wrong';
              reason = `Incorrect: expected "${expected}"`;
            }

            const gradeScore = grade === 'perfect' ? 3 : grade === 'partial' ? 1 : 0;
            const bestScore = bestGrade === 'perfect' ? 3 : bestGrade === 'partial' ? 1 : 0;
            if (
              gradeScore > bestScore ||
              (gradeScore === bestScore && distance < bestDistance)
            ) {
              bestGrade = grade;
              bestReason = reason;
              bestExpected = expected;
              bestDistance = distance;
            }
          });

          const isCorrect = isPassingGrade(bestGrade);

          return {
            blankId: blank.blankId,
            expectedAnswer: expectedRaw,
            providedAnswer: provided,
            isCorrect,
            grade: bestGrade,
            reason: bestReason,
          };
        }
      );

      return {
        sentenceId: sentence.sentenceId,
        blanks: blankEvaluations,
      };
    });

    const flattened = sentenceEvaluations.flatMap((sentence) => sentence.blanks);
    const totalCorrect = flattened.filter((blank) => blank.isCorrect).length;
    const totalBlanks = flattened.length;
    const maxScore = getNormalizedMaxScore(definition, Math.max(1, totalBlanks));

    return {
      score: totalBlanks === 0 ? 0 : Math.round((totalCorrect / totalBlanks) * maxScore),
      maxScore,
      isFullyCorrect: totalBlanks > 0 && totalCorrect === totalBlanks,
      details: {
        kind: 'FillInBlank',
        sentences: sentenceEvaluations,
      },
    };
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    const sentences = Array.isArray(details.sentences) ? details.sentences : [];

    return {
      sentences: sentences.map((sentence: any, index: number) => ({
        sentenceId: sentence?.sentenceId,
        sentence: sentence?.sentence ?? '___',
        order: sentence?.order ?? index,
        correction: sentence?.correction ?? '',
        blanks: (Array.isArray(sentence?.blanks) ? sentence.blanks : []).map((blank: any, blankIndex: number) => ({
          blankId: blank?.blankId,
          position: blank?.position ?? blankIndex,
          answer: blank?.answer ?? '',
          correction: blank?.correction ?? '',
        })),
      })),
    };
  },

  createDefault: (order) => ({
    type: 'FillInBlank' as const,
    sentences: [
      {
        sentenceId: undefined,
        sentence: '___',
        order: 0,
        correction: '',
        blanks: [
          { blankId: undefined, position: 0, answer: '', correction: '' },
        ],
      },
    ],
  }),
};
